/**
 * Shared markdown <-> HTML conversion for the program-document editor.
 *
 * Canonical storage is markdown. Quill operates on HTML. On load we render
 * markdown → HTML via `marked`; on save we convert HTML → markdown via
 * `turndown`. Asset embeds use a custom URI scheme (`asset://{uuid}`) so
 * regeneration preserves image references.
 */
import { marked } from 'marked'
import TurndownService from 'turndown'

const ASSET_URL_PREFIX = '/api/documents/assets/'

marked.setOptions({
  gfm: true,
  breaks: false,
})

const turndown = new TurndownService({
  headingStyle: 'atx',
  bulletListMarker: '-',
  codeBlockStyle: 'fenced',
  emDelimiter: '*',
})

// Keep images so we can rewrite the src URLs to asset:// form.
turndown.addRule('asset-image', {
  filter: 'img',
  replacement: (_content, node) => {
    const el = node as HTMLImageElement
    const src = el.getAttribute('src') || ''
    const alt = el.getAttribute('alt') || ''
    const rewritten = src.startsWith(ASSET_URL_PREFIX)
      ? `asset://${src.slice(ASSET_URL_PREFIX.length).replace(/\/$/, '')}`
      : src
    return `![${alt}](${rewritten})`
  },
})

// Strip runs of empty paragraphs that Quill emits between block elements.
turndown.addRule('drop-empty-p', {
  filter: (node) => node.nodeName === 'P' && !(node as HTMLElement).textContent?.trim(),
  replacement: () => '',
})

/** Render markdown → HTML for initial Quill load. */
export function markdownToHtml(markdown: string): string {
  if (!markdown) return ''
  const out = marked.parse(markdown)
  const html = typeof out === 'string' ? out : ''
  return rewriteAssetUrisToUrls(html)
}

/** Convert Quill HTML → canonical markdown for save. */
export function htmlToMarkdown(html: string): string {
  if (!html) return ''
  return turndown.turndown(html).trim()
}

function rewriteAssetUrisToUrls(html: string): string {
  return html.replace(
    /src=["']asset:\/\/([0-9a-f-]{36})["']/gi,
    (_m, uuid) => `src="${ASSET_URL_PREFIX}${uuid}/"`,
  )
}

/** Toolbar configuration constrained to markdown-safe features. */
export const MARKDOWN_SAFE_TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'strike'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['blockquote', 'code-block'],
  ['link', 'image'],
  ['clean'],
]
